import { ORBIT_LOGO } from '@orbit-mind/shared-types';
/**
 * The icon set.
 *
 * Hand-drawn paths rather than a dependency, and the reason is the same one that kept the undo
 * arrow a glyph: this application needs about fifteen icons, and fifteen icons do not justify a
 * package, a tree-shaking configuration and a second set of naming conventions. They are all
 * drawn on the same 24-unit grid with the same 1.75 stroke, which is what makes them look like
 * one family rather than fifteen decisions.
 *
 * Every icon is `aria-hidden`: an icon is never the accessible name of anything. The control
 * around it carries the name, which is why every icon button in this application takes a `label`
 * it cannot omit.
 */
export interface IconProps {
  /** In `rem`-independent pixels. 20 is the chrome default; 24 for a primary control. */
  readonly size?: number;
  readonly className?: string;
}

function Svg({
  size = 20,
  className,
  children,
}: IconProps & { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

export function IconUnlock(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 7.5-2M12 15v2" />
    </Svg>
  );
}
export function IconHand(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M8 12V5a2 2 0 0 1 4 0v7-8a2 2 0 0 1 4 0v8-6a2 2 0 0 1 4 0v9c0 4-3 7-7 7-3 0-5-2-7-5l-2-4a2 2 0 0 1 3-2l3 3" />
    </Svg>
  );
}
export function IconUndo(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 8h11a5 5 0 0 1 0 10H8" />
      <path d="m7 4-4 4 4 4" />
    </Svg>
  );
}

export function IconRedo(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M21 8H10a5 5 0 0 0 0 10h6" />
      <path d="m17 4 4 4-4 4" />
    </Svg>
  );
}

export function IconPlus(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

export function IconTrash(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </Svg>
  );
}

export function IconClose(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  );
}

/** The outline: a tree seen as text. */
export function IconOutline(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 6h4M4 6v12h4M8 12H4" />
      <path d="M11 6h9M11 12h9M11 18h9" />
    </Svg>
  );
}

/** The inspector: what is true about one thing. */
export function IconInspector(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M14 4v16M17 9h1M17 13h1" />
    </Svg>
  );
}

export function IconLayers(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 14 9 5 9-5" />
    </Svg>
  );
}

export function IconDownload(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M4 20h16" />
    </Svg>
  );
}

/** Fit to screen. */
export function IconFit(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4" />
    </Svg>
  );
}

export function IconZoomIn(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M11 8v6M8 11h6M16.5 16.5 21 21" />
    </Svg>
  );
}

export function IconZoomOut(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M8 11h6M16.5 16.5 21 21" />
    </Svg>
  );
}

/** More: the control that opens everything that did not fit. */
export function IconMore(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </Svg>
  );
}

export function IconLink(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 1 0-5.66-5.66l-1.5 1.5" />
      <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 1 0 5.66 5.66l1.5-1.5" />
    </Svg>
  );
}

export function IconFlip(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3v18" />
      <path d="M8 8 4 12l4 4M16 8l4 4-4 4" />
    </Svg>
  );
}

export function IconPaperclip(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M20 11.5 12 19.5a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 1 1-3-3l7-7" />
    </Svg>
  );
}

export function IconSearch(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 4.5 4.5" />
    </Svg>
  );
}

export function IconBack(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M19 12H5" />
      <path d="m11 6-6 6 6 6" />
    </Svg>
  );
}

export function IconPresent(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M12 16v4M8 20h8" />
    </Svg>
  );
}

export function IconCheck(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="m5 13 4 4L19 7" />
    </Svg>
  );
}

/** The map itself, as a mark. Also the product's glyph in the library header. */
export function IconAccount(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </Svg>
  );
}

export function IconSignOut(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 8l-4 4 4 4" />
      <path d="M6 12h9" />
    </Svg>
  );
}

export function IconOrbit({ size = 20, className }: IconProps): React.JSX.Element {
  return (
    <img
      src={ORBIT_LOGO.webMark}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={className}
      style={{ objectFit: 'contain' }}
    />
  );
}

export function IconAddChild(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="2" y="9" width="8" height="6" rx="1.5" />
      <path d="M10 12h4" />
      <path d="M17 12h5M19.5 9.5v5" />
    </Svg>
  );
}

export function IconAddSibling(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="8" y="2" width="8" height="6" rx="1.5" />
      <path d="M12 8v4" />
      <path d="M12 15v5M9.5 17.5h5" />
    </Svg>
  );
}

export function IconPalette(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 21a9 9 0 1 1 9-9c0 2-1.6 2.5-3 2.5h-1.5a2 2 0 0 0-1.3 3.5A1.8 1.8 0 0 1 12 21Z" />
      <circle cx="7.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="9.5" cy="7.8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="7.8" r="1.1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Shape: three outlines overlapping, which is what a shape picker is choosing between. */
export function IconShape(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="11" height="11" rx="2" />
      <circle cx="15.5" cy="15.5" r="5.5" />
    </Svg>
  );
}

export function IconGroup(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
      <rect x="8.5" y="8.5" width="7" height="7" rx="1.5" />
    </Svg>
  );
}

/** Reattach: this node, moved under a different parent. */
export function IconReattach(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="2.5" y="3" width="7" height="5" rx="1.5" />
      <rect x="14.5" y="16" width="7" height="5" rx="1.5" />
      <path d="M6 8v5a3 3 0 0 0 3 3h3" />
      <path d="m10 15 2.5 3.5" strokeDasharray="2.5 2.5" />
    </Svg>
  );
}

export function IconCollapse(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 6 4 12l5 6M15 6l5 6-5 6" />
    </Svg>
  );
}

export function IconExpand(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="m4 6 5 6-5 6M20 6l-5 6 5 6" />
    </Svg>
  );
}

/** A label on a line. The serif T is the universal mark for "put words here". */
export function IconLabel(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 6.5V5h14v1.5M12 5v14M9 19h6" />
    </Svg>
  );
}

/** Line style: the three kinds, stacked. */
export function IconLineStyle(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M3 6h18" />
      <path d="M3 12h18" strokeDasharray="4 3" />
      <path d="M3 18h18" strokeDasharray="1.5 3" />
    </Svg>
  );
}

/** Ends: what each end of a line is decorated with. */
export function IconEnds(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M6 12h12" />
      <path d="m14 8 4 4-4 4" />
      <circle cx="5" cy="12" r="2.5" />
    </Svg>
  );
}

export function IconEmoji(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
      <circle cx="9" cy="9.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="9.5" r="1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

export function IconNote(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 3h9l5 5v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5M8 13h8M8 17h5" />
    </Svg>
  );
}

export function IconDuplicate(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 9V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h4" />
      <path d="M14.5 14.5h5M17 12v5" />
    </Svg>
  );
}

export function IconCopy(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 9V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h4" />
    </Svg>
  );
}

export function IconCut(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="18" r="3" />
      <path d="M8.5 15.5 20 4M15.5 15.5 4 4" />
    </Svg>
  );
}

export function IconPaste(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 4H7a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-2" />
      <rect x="9" y="2.5" width="6" height="3.5" rx="1" />
      <path d="M9 12h6M9 16h4" />
    </Svg>
  );
}

/** Resize: a box with a handle being pulled. */
export function IconResize(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="12" height="12" rx="2" />
      <path d="M15 15h6v6h-6z" fill="currentColor" stroke="none" opacity="0.25" />
      <path d="M21 21h-6v-6M21 21l-4-4" />
    </Svg>
  );
}
